import type { TenantConfig } from '@/types'

// Configuration state
let apiBaseUrl = ''
let apiKey = ''
let theme = 'light'
let showHeader = true
let portalName = 'Advertiser Portal'
let demoEnabled = false
let tenantId: string | null = null
let siteId: string | null = null
let configLoaded = false
let sessionToken: string | null = null

// Check if we're in development mode
export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

// Check if running on Vite dev server
export function isViteDevServer(): boolean {
  return typeof import.meta.env.DEV === 'boolean' && import.meta.env.DEV
}

/**
 * Load configuration from the server based on tenant/site URL parameters.
 */
export async function loadServerConfig(): Promise<void> {
  if (configLoaded) return

  const urlParams = new URLSearchParams(window.location.search)
  tenantId = urlParams.get('t')
  siteId = urlParams.get('s')

  // If we have tenant parameters, load from server
  if (tenantId) {
    try {
      let configUrl = `/api/tenant-config?t=${encodeURIComponent(tenantId)}`
      if (siteId) {
        configUrl += `&s=${encodeURIComponent(siteId)}`
      }

      const response = await fetch(configUrl)
      if (response.ok) {
        const config: TenantConfig = await response.json()
        apiBaseUrl = config.apiBaseUrl || ''
        apiKey = config.apiKey || ''
        theme = config.theme || 'light'
        showHeader = config.showHeader ?? false // Default to false for embeds
        portalName = config.portalName || 'Advertiser Portal'
        demoEnabled = config.demoEnabled === true
        configLoaded = true
        console.log('Loaded tenant config:', { tenantId, siteId, apiBaseUrl, theme, showHeader })
        return
      }
    } catch (error) {
      console.error('Failed to load tenant config:', error)
    }
  }

  // In development, try to load from env var first, then localStorage
  if (isDevelopment()) {
    const envApiKey = import.meta.env.VITE_DEV_API_KEY
    if (envApiKey) {
      apiBaseUrl = ''
      apiKey = envApiKey
      showHeader = true
      configLoaded = true
      console.log('Loaded config from VITE_DEV_API_KEY env var')
      return
    }

    const storedConfig = localStorage.getItem('advertiserPortal_config')
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig)
        apiBaseUrl = config.apiBaseUrl || ''
        apiKey = config.apiKey || ''
        theme = config.theme || 'light'
        showHeader = config.showHeader ?? true
        portalName = config.portalName || 'Advertiser Portal'
        configLoaded = true
        console.log('Loaded config from localStorage')
        return
      } catch {
        console.error('Failed to parse stored config')
      }
    }
  }

  configLoaded = true
}

/**
 * Check if server config is available.
 */
export function hasServerConfig(): boolean {
  return tenantId !== null
}

/**
 * Check if in tenant mode (URL parameters present).
 */
export function isTenantMode(): boolean {
  return tenantId !== null
}

/**
 * Check if API is configured.
 */
export function isApiConfigured(): boolean {
  if (isDevelopment() && apiKey !== '') return true
  return apiBaseUrl !== '' && apiKey !== ''
}

// Getters
export function getApiBaseUrl(): string {
  return apiBaseUrl
}

export function getApiKey(): string {
  return apiKey
}

export function getTheme(): string {
  return theme
}

export function getShowHeader(): boolean {
  return showHeader
}

export function getPortalName(): string {
  return portalName
}

export function isDemoEnabled(): boolean {
  return demoEnabled
}

export function getTenantId(): string | null {
  return tenantId
}

// Session token management
export function getSessionToken(): string | null {
  if (!sessionToken) {
    sessionToken = sessionStorage.getItem('advertiserPortal_sessionToken')
  }
  return sessionToken
}

export function setSessionToken(token: string | null): void {
  sessionToken = token
  if (token) {
    sessionStorage.setItem('advertiserPortal_sessionToken', token)
  } else {
    sessionStorage.removeItem('advertiserPortal_sessionToken')
  }
}

export function clearSession(): void {
  setSessionToken(null)
  sessionStorage.removeItem('advertiserPortal_account')
}

export function isAuthenticated(): boolean {
  return getSessionToken() !== null
}

// Development config storage
export function saveDevConfig(config: { apiBaseUrl: string; apiKey: string; theme?: string }): void {
  localStorage.setItem('advertiserPortal_config', JSON.stringify(config))
  apiBaseUrl = config.apiBaseUrl
  apiKey = config.apiKey
  theme = config.theme || 'light'
}
